import { Injectable } from '@angular/core';
import { AuthService } from 'services/auth.service';
import { PouchDoc } from '@cf-shared/data-model/pouch';
import { CommonDbService } from './common-db.service';
import { DeferredPromise } from '@cf-shared/shared-util';
import { DocWatcher, DocChangeType } from './doc-watcher';
import { LoggingService, errorReportFields, isTransientFetchError } from 'services/logging.service';
import { waitForReadsToGoLocalAsync } from './user-db.task-status';
import { ConfigReloadOutcome, planConfigCacheUpdate, runConfigDbInitAsync } from './config-db-init';
import { DatabaseWrapper } from './database-wrapper';
import { isDatabaseTornDown } from './database-unavailable-error';
import { resolveConfigRecordForUser } from './config-record-resolve';
export { ConfigKey } from './config-keys';

const CONFIG_ID_PREFIX = 'config/';
// U+FFF0, above every character a document id uses: `prefix + this` ends a key range
// that covers every id starting with the prefix. Built from its code so the source
// holds no invisible character.
const HIGHEST_KEY_SUFFIX = String.fromCharCode(0xfff0);

export type ConfigWatcherCallback<T = any> = (newValue: T | undefined, oldValue: T | undefined) => void;

class ConfigVarRecord<Type> extends PouchDoc {
  globalValue: Type; // the value for all users that are not in the list
  userValues: { [userId: string]: Type }; // the value for specific users
}

@Injectable()
export class ConfigDbService {
  public loaded = DeferredPromise.defer<boolean>();
  private configVars: { [key: string]: any } = {};
  private configRecords = new Map<string, ConfigVarRecord<any>>(); // key (without prefix) → record reference
  private configWatchers = new Map<string, Set<ConfigWatcherCallback>>(); // key → subscriber callbacks
  private docWatcher: DocWatcher;
  private watchedDatabase?: DatabaseWrapper; // the global_common wrapper the watcher was created on
  private localCopyKeys: ReadonlySet<string> = new Set(); // #6089: keys last filled from the local copy
  private userId: string;

  constructor(private auth: AuthService, private commonDb: CommonDbService, private logging: LoggingService) {
  }

  async initializeAsync() {
    // L7 (#3986): run the real init under a try/catch. If it throws, resolve `loaded`
    // with false (so every getConfigValueAsync() await falls through to undefined instead
    // of hanging forever) and report the failure. Without this a config-DB open failure
    // froze every feature-flag-gated code path and leaked an unhandled rejection.
    //
    // #6089: a passing network drop during the boot read is recovered rather than
    // reported — the local copy is served at once, and the config is read again once
    // reads of global_common go to the local database. See config-db-init.ts.
    await runConfigDbInitAsync({
      runInit: () => this.loadConfigAsync(),
      resolveLoaded: ok => this.loaded.resolve(ok),
      reportFailure: err => {
        // if the database is closing (our own sign-out during the retry), this is not a fault
        if (isDatabaseTornDown(err)) {
          console.log('ConfigDbService: config not re-read, the database is closing');
          return;
        }
        console.error('ConfigDbService: initialization failed — config values will fall back to defaults', err);
        this.logging.trackSilentProblem('ConfigDbInitFailed', errorReportFields(err));
      },
      isTransient: isTransientFetchError,
      loadFromLocalCopyAsync: () => this.loadFromLocalCopyAsync(),
      waitForReadsLocalAsync: () => this.waitForCommonDbReadsLocalAsync(),
      reloadAsync: () => this.reloadConfigAsync(),
      noteDatabaseClosing: () => console.log('ConfigDbService: config not re-read, the user signed out'),
      reportRecovered: report => {
        this.logging.trackSilentInfo('ConfigDbInitRecovered', {
          servedFromLocalCopy: report.servedFromLocalCopy,
          ...errorReportFields(report.originalError),
          ...(report.localCopyError !== undefined ? { localCopyError: errorReportFields(report.localCopyError) } : {}),
        });
      },
      // df-5c87afe2dd40: the network stayed down through the retry, but the device's own
      // copy is serving the config, so this is not ConfigDbInitFailed
      reportServedLocalCopyOnly: report => {
        this.logging.trackSilentInfo('ConfigDbInitServedLocalCopy', {
          ...errorReportFields(report.originalError),
          retryError: errorReportFields(report.retryError),
        });
      },
    });
  }

  // The real config-DB initialization: open commonDb, read the config records, and
  // populate the in-memory cache. Any throw here is caught by runConfigDbInitAsync above.
  private async loadConfigAsync(): Promise<void> {
    await this.commonDb.initializeAsync();

    this.userId = this.auth.user.uid;

    // persistent watcher — lives for the app's lifetime, re-resolves values when config docs change via sync
    this.watchedDatabase = this.commonDb.database;
    this.docWatcher = this.watchedDatabase.createDocWatcher((changes) => {
      // #4358: RETURN it, never `void` it — the watcher uses the promise to
      // keep two config passes from overlapping.
      return this.handleConfigChanges(changes);
    }, { label: 'config-db' });

    const allVars = await this.watchedDatabase.getByIdHeadAsync(CONFIG_ID_PREFIX, this.docWatcher) as ConfigVarRecord<any>[];
    this.applyConfigRecords(allVars);
  }

  // #6089: the retry after a transient failure. It never opens global_common itself:
  // a sign-out during the wait has shut the database down, and opening a fresh one
  // then would hold it open while the login page clears local data.
  private async reloadConfigAsync(): Promise<ConfigReloadOutcome> {
    // if the user signed out while the retry waited, leave the closing database alone
    if (!this.auth.user) return ConfigReloadOutcome.DatabaseClosing;

    // if the boot open itself never finished, nothing is open yet: run the whole init again
    if (!this.watchedDatabase) {
      await this.loadConfigAsync();
      return ConfigReloadOutcome.Reloaded;
    }

    // if the database the watcher belongs to is closing or was replaced, it is not ours to read
    if (this.watchedDatabase.isShuttingDown || this.watchedDatabase !== this.commonDb.database) {
      return ConfigReloadOutcome.DatabaseClosing;
    }

    const allVars = await this.watchedDatabase.getByIdHeadAsync(CONFIG_ID_PREFIX, this.docWatcher) as ConfigVarRecord<any>[];
    this.applyConfigRecords(allVars);
    return ConfigReloadOutcome.Reloaded;
  }

  // #6089: fill the cache from the copy of global_common already on this device, never
  // the network. These are NOT the identity-mapped records `handleConfigChanges` relies
  // on, so the retry in runConfigDbInitAsync replaces them once reads go local.
  // Answers whether the local copy held any config documents.
  private async loadFromLocalCopyAsync(): Promise<boolean> {
    // if the open itself failed there is no database to read
    const database = this.commonDb.database;
    if (!database) return false;

    const localVars = await database.getByIdRangeLocalOnlyAsync(CONFIG_ID_PREFIX, CONFIG_ID_PREFIX + HIGHEST_KEY_SUFFIX) as ConfigVarRecord<any>[] | null;
    // null: the local read could not run (shutting down / recovering)
    if (!localVars || localVars.length === 0) return false;

    this.applyConfigRecords(localVars);
    // remember these keys: the retry may drop one the server has deleted since
    this.localCopyKeys = new Set(this.configRecords.keys());
    return true;
  }

  // #6089: wait until a read of global_common goes to the local database instead of the
  // server. A settled sync is not enough (df-5c87afe2dd40): a round that fails while
  // online keeps reads on the server, so only a success, or a failed round that closed
  // the gate because the device is offline, ends the wait. The wait gives up at its cap,
  // so the retry always runs. why this is not an await on the wrapper's promise: see
  // waitForInitialSyncAsync and design-docs/sync-integrity.md.
  private async waitForCommonDbReadsLocalAsync(): Promise<void> {
    // if the open itself failed there is no sync to wait for; the retry re-opens
    const database = this.commonDb.database;
    if (!database) return;
    await waitForReadsToGoLocalAsync(
      listener => database.onInitialSyncSettled(listener),
      () => database.readsPassThroughToServer,
    );
  }

  // Apply a full read of the config records to the cache, then notify subscribers of
  // each key whose value changed. The decision is the pure `planConfigCacheUpdate`; on
  // the first read nothing is cached yet, so every key is new.
  private applyConfigRecords(records: ConfigVarRecord<any>[]): void {
    const update = planConfigCacheUpdate(this.configVars, records, record => this.resolveValue(record), this.localCopyKeys);
    // the local-copy keys are settled by this read: only the read right after them may drop one
    this.localCopyKeys = new Set();

    for (const { key, record, value } of update.set) {
      this.configRecords.set(key, record);
      this.configVars[key] = value;
      console.log(`Config: ${key} = ${JSON.stringify(value)} (global=${JSON.stringify(record.globalValue)}, user=${JSON.stringify(record.userValues?.[this.userId])})`);
    }
    for (const key of update.drop) {
      this.configRecords.delete(key);
      delete this.configVars[key];
    }
    for (const { key, newValue, oldValue } of update.notify) {
      this.notifySubscribers(key, newValue, oldValue);
    }
  }

  getConfigValue<Type>(key: string): Type | undefined {
    return this.configVars[key];
  }

  // Returns the value as a promise, resolving when the config is loaded
  async getConfigValueAsync<Type>(key: string): Promise<Type | undefined> {
    await this.loaded;

    return this.getConfigValue<Type>(key);
  }

  // Subscribe to changes for a specific config key. Returns an unsubscribe function.
  watchConfigValue<T>(key: string, callback: ConfigWatcherCallback<T>): () => void {
    let subscribers = this.configWatchers.get(key);
    if (!subscribers) {
      subscribers = new Set();
      this.configWatchers.set(key, subscribers);
    }
    subscribers.add(callback);

    return () => {
      subscribers.delete(callback);
      if (subscribers.size === 0) {
        this.configWatchers.delete(key);
      }
    };
  }

  // Apply a config doc that was just written LOCALLY in this session (e.g. via the
  // Admin → Config page). A local write to global_common is pushed to the server but does
  // NOT flow back through the sync-pull change feed that normally refreshes this cache
  // (notifyDocWatchers only fires for sync-pulled changes from the server / another
  // device). Without this, an admin's own flag edit would not take effect until the next
  // app reload. Re-resolves the value from the record and notifies subscribers so live
  // gates (e.g. the recipe-scanner ⋯ menu) update immediately. Safe for both edits to a
  // known config and brand-new config docs created this session.
  applyLocalConfigWrite(record: ConfigVarRecord<any>): void {
    const key = record._id.match(/^config\/(.*)/)?.[1];
    if (!key) return;

    this.configRecords.set(key, record);
    const newValue = this.resolveValue(record);
    const oldValue = this.configVars[key];
    if (newValue === oldValue) return;

    console.log(`Config applied locally: ${key} = ${JSON.stringify(newValue)} (was ${JSON.stringify(oldValue)})`);
    this.configVars[key] = newValue;
    this.notifySubscribers(key, newValue, oldValue);
  }

  // The rule itself lives in an import-free module because #4981 phase 3's
  // migration re-reads two config documents on the boot path, before this service
  // exists, and must resolve them EXACTLY as this service would. It cannot import
  // this file — this file reaches `pouchdb-browser`, which does not load under
  // plain node — so the rule moved rather than being copied. See
  // `config-record-resolve.ts` for why the presence test is `!== undefined`.
  private resolveValue(record: ConfigVarRecord<any>): any {
    return resolveConfigRecordForUser(record, this.userId);
  }

  private async handleConfigChanges(changes: import('./doc-watcher').DocChange[]): Promise<void> {
    for (const change of changes) {
      if (change.type === DocChangeType.Changed) {
        // identity-map object is already updated in place by DatabaseWrapper
        const key = change.id.match(/^config\/(.*)/)?.[1];
        if (!key) continue;

        const record = this.configRecords.get(key);
        if (!record) continue;

        const newValue = this.resolveValue(record);
        const oldValue = this.configVars[key];
        if (newValue !== oldValue) {
          console.log(`Config changed: ${key} = ${JSON.stringify(newValue)} (was ${JSON.stringify(oldValue)})`);
          this.configVars[key] = newValue;
          this.notifySubscribers(key, newValue, oldValue);
        }
      } else if (change.type === DocChangeType.New) {
        // new config doc created after startup
        if (!change.id.startsWith('config/')) continue;

        const key = change.id.match(/^config\/(.*)/)?.[1];
        if (!key || this.configRecords.has(key)) continue;

        try {
          const record = await this.commonDb.database.getRecordAsync(change.id, this.docWatcher) as ConfigVarRecord<any>;
          this.configRecords.set(key, record);
          const newValue = this.resolveValue(record);
          this.configVars[key] = newValue;
          this.notifySubscribers(key, newValue, undefined);
        } catch (err) {
          // if the database is closing (our own sign-out), this is not a fault
          if (isDatabaseTornDown(err)) {
            console.log(`ConfigDbService: new config doc "${change.id}" not loaded, the database is closing`);
            continue;
          }
          // otherwise the app keeps running without this config value, and nothing
          // retries the load until the next start-up (#5191)
          this.logging.trackSilentProblem('ConfigDocLoadFailed', { docId: change.id, ...errorReportFields(err) });
        }
      }
    }
  }

  private notifySubscribers(key: string, newValue: any, oldValue: any): void {
    const subscribers = this.configWatchers.get(key);
    if (!subscribers) return;
    for (const callback of subscribers) {
      callback(newValue, oldValue);
    }
  }
}
